import { useContext, useCallback } from 'react';
import { AuthContext } from '../contexts/AuthContext';

export interface User {
  id: number;
  username: string;
  email: string;
  nickname?: string;
  roles?: string[];
  role?: string;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  const { user, userRoles, loading, login, logout, isAuthenticated, refetch } = context;

  const hasRole = useCallback((roleName: string): boolean => {
    if (userRoles.includes(roleName)) return true;
    if (user?.roles && Array.isArray(user.roles) && user.roles.includes(roleName)) return true;
    if (user?.role === roleName) return true;
    return false;
  }, [userRoles, user]);

  const hasAnyRole = useCallback((roleNames: string[]): boolean => {
    return roleNames.some(role => hasRole(role));
  }, [hasRole]);

  const isAdmin = useCallback((): boolean => {
    if (hasRole('ROLE_ADMIN')) return true;
    if (user?.role === 'ROLE_ADMIN' || user?.role === 'ADMIN') return true;
    if (user?.username === 'admin') return true;
    return false;
  }, [hasRole, user]);

  const isEditor = useCallback((): boolean => {
    if (hasRole('ROLE_EDITOR')) return true;
    if (user?.role === 'ROLE_EDITOR' || user?.role === 'EDITOR') return true;
    return false;
  }, [hasRole, user]);

  const isUser = useCallback((): boolean => {
    if (hasRole('ROLE_USER')) return true;
    if (user?.role === 'ROLE_USER' || user?.role === 'USER') return true;
    return false;
  }, [hasRole, user]);

  const isAnonymous = useCallback((): boolean => {
    return !user && (hasRole('ROLE_ANONYMOUS') || userRoles.length === 0);
  }, [user, userRoles, hasRole]);

  const isLoggedIn = useCallback((): boolean => {
    return !isAnonymous() && user !== null;
  }, [isAnonymous, user]);

  // 메뉴 접근 권한 확인
  const canAccessMenu = useCallback((menuName: string): boolean => {
    switch (menuName) {
      case 'admin':
      case 'dashboard':
      case 'category-manage':
        return isAdmin();
      case 'content-create':
        return isAdmin() || isEditor() || isUser();
      case 'timeline':
      case 'topic-areas':
        return isLoggedIn();
      case 'notice':
        return true;
      case 'free':
      case 'debate':
        return isLoggedIn();
      case 'mypage':
      case 'profile':
      case 'notifications':
        return isLoggedIn();
      default:
        return false;
    }
  }, [isAdmin, isEditor, isUser, isLoggedIn]);

  // 컨텐츠 수정/삭제 권한 확인
  const canModifyContent = useCallback((contentUserId: number): boolean => {
    if (isAdmin()) return true;
    if (isEditor()) return true;
    return user?.id === contentUserId;
  }, [isAdmin, isEditor, user]);

  // 게시판 수정/삭제 권한 확인
  const canModifyBoard = useCallback((boardUserId: number, boardCategory: string): boolean => {
    if (isAdmin()) return true;
    if (boardCategory === 'notice') return isAdmin();
    return user?.id === boardUserId;
  }, [isAdmin, user]);

  // 주제영역 소유자 여부 확인
  const isTopicAreaOwner = useCallback((createdBy: number): boolean => {
    return user?.id === createdBy;
  }, [user]);

  return {
    user,
    userRoles,
    loading,
    hasRole,
    hasAnyRole,
    isAdmin,
    isEditor,
    isUser,
    isAnonymous,
    isLoggedIn,
    canAccessMenu,
    canModifyContent,
    canModifyBoard,
    isTopicAreaOwner,
    refetch
  };
}; 